// src/components/auth/Login.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/useApi';

const Login = () => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { login } = useAuth();
    const { post } = useApi();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await post(
                '/api/auth/login/',
                {
                    email: formData.email,
                    password: formData.password,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true,
                }
            );

            if (!response.user || !response.user.id) {
                throw new Error('User ID not found in login response');
            }

            localStorage.setItem('access_token', response.access);
            localStorage.setItem('refresh_token', response.refresh);
            login(response.user, response.access, response.refresh);

            // RBAC Redirect
            const { role } = response.user;
            if (role === 'admin') {
                navigate('/admin/dashboard');
            } else if (role === 'teacher') {
                navigate('/teacher/dashboard');
            } else if (role === 'parent') {
                navigate('/parent/dashboard');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            let errorMessage = 'Invalid credentials. Please try again.';
            if (err.response?.data) {
                const data = err.response.data;
                if (typeof data === 'string') {
                    errorMessage = data;
                } else if (data.detail) {
                    errorMessage = data.detail;
                } else if (data.error) {
                    errorMessage = data.error;
                } else if (data.non_field_errors) {
                    errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(' ') : data.non_field_errors;
                } else {
                    // Extract field-specific errors if available
                    const messages = [];
                    for (const [key, value] of Object.entries(data)) {
                        if (Array.isArray(value)) {
                            messages.push(`${key}: ${value.join(' ')}`);
                        } else if (typeof value === 'string') {
                            messages.push(`${key}: ${value}`);
                        }
                    }
                    if (messages.length > 0) errorMessage = messages.join(' | ');
                }
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4 md:p-6 lg:p-12 relative overflow-hidden" style={{ background: 'linear-gradient(150deg, #FFFFFF 0%, #F5D97A 100%)', backgroundAttachment: 'fixed' }}>
            {/* Soft Ambient Particles - Hidden on mobile for performance */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none hidden lg:block">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-white/40 rounded-full blur-[120px] animate-pulse" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-white/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '3s' }} />
            </div>

            <div className="w-full max-w-xl relative z-10 transition-all duration-1000 animate-in fade-in slide-in-from-bottom-12">
                {/* Logo and Welcome Header */}
                <div className="text-center mb-2">
                    <div className="inline-flex items-center justify-center p-2 mb-1 group transition-all hover:scale-110 active:scale-95">
                        <img 
                            src="/kianda-school-logo-removebg-preview.png" 
                            alt="Kianda School Logo" 
                            className="h-24 lg:h-32 w-auto drop-shadow-2xl"
                        />
                    </div>
                    <h1 className="text-4xl lg:text-5xl font-black text-[#18216D] mb-1 tracking-tight">Welcome Back</h1>
                    <p className="text-[#18216D]/60 font-bold text-sm lg:text-base">
                        Enter your credentials to access the portal
                    </p>
                </div>

                <div className="bg-white/70 backdrop-blur-3xl rounded-[2rem] lg:rounded-[3rem] p-6 md:p-8 lg:p-14 shadow-[0_32px_120px_-20px_rgba(245,217,122,0.15)] border border-white/60 relative overflow-hidden group/card hover:shadow-[0_32px_120px_-20px_rgba(245,217,122,0.25)] transition-shadow duration-700">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/30 rounded-full -mr-32 -mt-32 blur-3xl group-hover/card:scale-110 transition-transform duration-1000" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/20 rounded-full -ml-32 -mb-32 blur-3xl group-hover/card:scale-110 transition-transform duration-1000" style={{ transitionDelay: '0.2s' }} />

                    {error && (
                        <div className="mb-6 p-4 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl text-sm font-bold flex items-center gap-3">
                            <i className="fas fa-circle-exclamation"></i>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#18216D] block mb-2 ml-1">Email or Student ID</label>
                            <div className="relative group/input">
                                <i className={`${formData.email && !formData.email.includes('@') ? 'fas fa-id-badge' : 'far fa-envelope'} absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within/input:text-[#18216D] transition-colors duration-300`}></i>
                                <input
                                    type="text"
                                    name="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={loading}
                                    className="w-full pl-12 lg:pl-16 pr-6 py-4 lg:py-5 bg-slate-50 border-2 border-slate-200 rounded-[1.25rem] focus:bg-white focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 hover:border-slate-300 transition-all duration-300 text-sm font-bold text-[#18216D] placeholder-slate-400 shadow-sm"
                                    placeholder="e.g. name@kianda.ac.ke or 4785"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2 ml-1">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#18216D]">Password</label>
                                <Link to="/forgot-password" className="text-[10px] font-black uppercase tracking-widest text-[#18216D]/60 hover:text-[#18216D]">Forgot Password?</Link>
                            </div>
                            <div className="relative group/input">
                                <i className="fas fa-lock absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within/input:text-[#18216D] transition-colors duration-300"></i>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    disabled={loading}
                                    className="w-full pl-12 lg:pl-16 pr-16 py-4 lg:py-5 bg-slate-50 border-2 border-slate-200 rounded-[1.25rem] focus:bg-white focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 hover:border-slate-300 transition-all duration-300 text-sm font-bold text-[#18216D] placeholder-slate-400 shadow-sm"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#18216D] hover:bg-slate-100 rounded-lg p-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#18216D]/20"
                                >
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`group/btn w-full py-5 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-900/30 hover:bg-[#0D164F] hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-4 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? (
                                <div className="flex items-center gap-3">
                                    <i className="fas fa-circle-notch fa-spin"></i>
                                    <span>Processing...</span>
                                </div>
                            ) : (
                                <>
                                    <span className="ml-4">Sign In</span>
                                    <i className="fas fa-arrow-right group-hover/btn:translate-x-2 transition-transform duration-300"></i>
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-10 pt-10 border-t border-slate-50 text-center">
                        <p className="text-slate-400 text-sm font-medium">
                            New user? <Link to="/signup" className="text-[#18216D] font-black hover:underline underline-offset-4">Create Institution Account</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
